import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { StageTimeline } from "../components/shared/StageTimeline";
import { WhatDoesThisMean } from "../components/shared/WhatDoesThisMean";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { getClientNavItems } from "../modules/clientNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { SUPPORT_EMAIL } from "../constants/brand";

const STAGES = ["Inquiry", "In Review", "Documents Sent", "Active Trust"];

const STAGE_EXPLAINERS = {
  Inquiry: "We've received your request and are reviewing your situation to determine the best trust structure for you.",
  "In Review": "Our team is analyzing your assets and goals to build a trust structure that protects what matters most.",
  "Documents Sent": "Your trust documents are ready and have been sent to you for review and signature.",
  "Active Trust": "Your trust is fully set up and active — your assets are now protected under this structure.",
};

export default function ClientTrustPage() {
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/trust/status");
      setRecord(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const requestService = async () => {
    try {
      await api.post("/trust/request");
      toast.success("Your inquiry has been submitted");
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const navItems = getClientNavItems(user?.unlocked_modules);

  return (
    <DashboardLayout navItems={navItems} moduleName="Trust & Asset Protection" moduleBackPath="/client">
      <div data-testid="client-trust-page" className="max-w-2xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Set up trust or asset protection structuring with IGC and track your request's progress.">Trust & Asset Protection</h1>
        <p className="text-igc-muted text-sm mb-10">Protect your business and personal assets through IGC's proprietary trust structuring process.</p>

        {loading ? null : !record ? (
          <div className="bg-igc-surface border border-igc-gold/15 p-10 text-center" data-testid="trust-get-started">
            <p className="text-igc-cream mb-6">You haven't started a Trust & Asset Protection inquiry yet.</p>
            <Button data-testid="trust-request-button" onClick={requestService} title="Starts your Trust & Asset Protection inquiry with IGC." className="bg-igc-gold hover:bg-igc-goldHover text-black px-8">
              Get Started
            </Button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-igc-surface border border-igc-gold/15 p-10" data-testid="trust-status-card">
            <StageTimeline stages={STAGES} currentStage={record.stage} testId="trust-stage-timeline" />
            <p className="text-igc-goldText text-center mt-10 font-serif text-xl" data-testid="trust-current-stage">
              {record.stage}
              <WhatDoesThisMean testId="trust-stage-explainer" text={STAGE_EXPLAINERS[record.stage] || "Your Trust & Asset Protection request is being handled by our team."} />
            </p>
            {record.updated_at && (
              <p className="text-igc-muted text-xs text-center mt-2" data-testid="trust-last-updated">
                Last updated {formatET(record.updated_at, "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </motion.div>
        )}

        <p className="text-igc-muted text-xs text-center mt-10">Questions? Contact us at {SUPPORT_EMAIL}</p>
      </div>
    </DashboardLayout>
  );
}
